import { useState } from 'react';
import clsx from 'clsx';

export interface SkippedMarketDataEntry {
  marketField: string;
  reason: string;
}

export interface AppliedMarketDataItem {
  inputName: string;
  marketField: string;
  source: string;
}

export interface SkippedMarketDataBadgeProps {
  /**
   * Latest `apply_market_defaults.skipped[]` payload (from the most recent
   * tool call this turn). When empty/undefined, the badge renders nothing.
   * Surfaces market-data categories that were intentionally skipped (no
   * coverage, source gate closed, all-null values) so the user understands
   * the gap before reviewing variables.
   */
  skipped?: SkippedMarketDataEntry[];
  /**
   * Apply-result transparency accounting from `apply_market_defaults` Phase 3.
   * When present and `fetched > 0`, renders a "N fetched · M applied · K no
   * target" line so the user can see how many market fields were successfully
   * applied vs. had no matching model input.
   */
  fetchedVsApplied?: {
    fetched: number;
    applied: number;
    noTarget: number;
  };
  /**
   * Itemized list of applied fields (WIN 1, 2026-06-10). When present and
   * non-empty, the "M applied" segment in the accounting line becomes an
   * expandable toggle showing `{humanized inputName} ← {humanized marketField}`
   * per row. Separate toggle state from the skip-list toggle.
   */
  appliedItems?: AppliedMarketDataItem[];
  className?: string;
}

/**
 * Humanize a camelCase / snake_case field name for display.
 * e.g. "permanent_loan_rate" → "Permanent Loan Rate"
 */
function humanize(field: string): string {
  return field
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Collapsible badge inside the `<VariablesPanel>` header. Renders only
 * when `skipped` is non-empty OR `fetchedVsApplied` is present. Click
 * expands to show per-category skip reasons.
 */
export function SkippedMarketDataBadge({
  skipped,
  fetchedVsApplied,
  appliedItems,
  className,
}: SkippedMarketDataBadgeProps) {
  const [skipExpanded, setSkipExpanded] = useState(false);
  const [appliedExpanded, setAppliedExpanded] = useState(false);

  // Relaxed guard: render when EITHER skipped entries exist OR the accounting
  // summary is present (a fully-applied result still wants the transparency
  // line even with zero skips).
  if ((!skipped || skipped.length === 0) && !fetchedVsApplied) return null;

  const count = skipped?.length ?? 0;
  const label = count === 1 ? '1 unavailable' : `${count} unavailable`;
  const showAccountingLine =
    fetchedVsApplied !== undefined && fetchedVsApplied.fetched > 0;
  const hasAppliedItems = appliedItems !== undefined && appliedItems.length > 0;

  return (
    <div
      data-testid="skipped-market-data-badge"
      className={clsx(
        'rounded-[2px] border border-warn/15 bg-warn-dim/30 text-warn',
        className
      )}
    >
      {showAccountingLine && (
        <div
          data-testid="skipped-market-data-accounting"
          className="px-2 pt-1 pb-0.5 text-micro font-mono text-text-muted"
        >
          {fetchedVsApplied.fetched} fetched ·{' '}
          {hasAppliedItems ? (
            <button
              type="button"
              onClick={() => setAppliedExpanded((v) => !v)}
              data-testid="applied-market-data-toggle"
              data-expanded={appliedExpanded ? 'true' : 'false'}
              aria-expanded={appliedExpanded}
              className="inline underline decoration-dotted cursor-pointer bg-transparent border-none text-inherit font-inherit p-0"
            >
              {fetchedVsApplied.applied} applied
            </button>
          ) : (
            <span>{fetchedVsApplied.applied} applied</span>
          )}{' '}
          · {fetchedVsApplied.noTarget} no target
        </div>
      )}
      {showAccountingLine && hasAppliedItems && appliedExpanded && (
        <ul
          data-testid="applied-market-data-list"
          className="px-2 pb-1 pt-0 space-y-0.5"
        >
          {appliedItems!.map((item) => (
            <li
              key={`${item.inputName}:${item.marketField}`}
              data-testid="applied-market-data-row"
              data-field={item.inputName}
              className="text-micro font-mono text-text-muted"
            >
              <span className="text-text">{humanize(item.inputName)}</span>
              <span className="text-text-dim"> ← </span>
              <span className="text-text-muted">
                {humanize(item.marketField)}
              </span>
            </li>
          ))}
        </ul>
      )}
      {count > 0 && (
        <>
          <button
            type="button"
            onClick={() => setSkipExpanded((v) => !v)}
            className="w-full px-2 py-1 flex items-center justify-between gap-2 cursor-pointer bg-transparent border-none"
            data-testid="skipped-market-data-toggle"
            data-expanded={skipExpanded ? 'true' : 'false'}
            aria-expanded={skipExpanded}
          >
            <span className="text-micro font-mono font-semibold tracking-[0.4px] uppercase">
              ⚠ Market data {label}
            </span>
            <span className="text-micro font-mono">
              {skipExpanded ? '▲' : '▼'}
            </span>
          </button>
          {skipExpanded && (
            <ul
              data-testid="skipped-market-data-list"
              className="px-2 pb-1.5 pt-0.5 space-y-0.5"
            >
              {skipped!.map((entry) => (
                <li
                  key={entry.marketField}
                  data-testid="skipped-market-data-row"
                  data-field={entry.marketField}
                  className="text-micro font-mono"
                >
                  <span className="text-warn font-semibold">
                    {entry.marketField}
                  </span>
                  <span className="text-text-dim"> — {entry.reason}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
